import { FC, useEffect } from "react";
import { ChakraProvider } from "@chakra-ui/react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { useNavigate, useLocation } from "react-router-dom";
import { theme } from "./Theme";
import { AuthenticatedRoutes, UnauthenticatedRoutes } from "./Routes";
import { KeycloakProvider, useKeycloak } from "@Contexts/KeycloakContext";
import { CompanyProvider } from "@Contexts/CompanyContext";
import { UserAuthorizationProvider } from "@Contexts/PermissionsContext";
import {
  setSessionExpiredCallback,
  setForbiddenCallback,
} from "@Config/api/clientFleetManager";
import useToast from "@Components/Toast";

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
    },
  },
});

const PUBLIC_PATHS = ["/login", "/logout", "/authorization-error"];

const AppContent: FC = () => {
  const {
    showSessionExpired,
    isAuthenticated,
    isLoading,
    pendingNavigation,
    clearPendingNavigation,
  } = useKeycloak();
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();

  useEffect(() => {
    setSessionExpiredCallback(showSessionExpired);
  }, [showSessionExpired]);

  useEffect(() => {
    setForbiddenCallback((message: string) => {
      toast({
        type: "warning",
        message:
          message || "Você não possui permissão para realizar esta ação.",
      });
    });
  }, [toast]);

  useEffect(() => {
    if (isAuthenticated && pendingNavigation) {
      navigate(pendingNavigation);
      clearPendingNavigation();
    }
  }, [isAuthenticated, pendingNavigation, navigate, clearPendingNavigation]);

  // Rotas públicas: sempre acessíveis sem autenticação.
  if (PUBLIC_PATHS.includes(location.pathname)) {
    return <UnauthenticatedRoutes />;
  }

  if (isLoading) {
    return null;
  }

  if (!isAuthenticated) {
    return null;
  }

  return (
    <UserAuthorizationProvider>
      <AuthenticatedRoutes />
    </UserAuthorizationProvider>
  );
};

const App: FC = () => {
  return (
    <ChakraProvider theme={theme}>
      <QueryClientProvider client={queryClient}>
        <KeycloakProvider>
          <CompanyProvider>
            <AppContent />
          </CompanyProvider>
        </KeycloakProvider>
      </QueryClientProvider>
    </ChakraProvider>
  );
};

export default App;
